import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Save, Settings as SettingsIcon, Globe, Mail, Phone, MapPin, AlertCircle, Power } from 'lucide-react';
import PageHeader from '../../components/admin/PageHeader';
import ContentCard from '../../components/admin/ContentCard';
import LoadingState from '../../components/admin/LoadingState';
import toast from 'react-hot-toast';

interface SettingsData {
  id: string;
  companyName: string;
  email: string;
  phone: string;
  address: string;
  updatedAt?: string;
}

interface MaintenanceSettings {
  enabled: boolean;
  message: string;
}

const Settings: React.FC = () => {
  const [settings, setSettings] = useState<SettingsData>({
    id: '',
    companyName: '',
    email: '',
    phone: '',
    address: ''
  });
  const [maintenance, setMaintenance] = useState<MaintenanceSettings>({
    enabled: false,
    message: 'Notre site est actuellement en maintenance pour vous offrir une meilleure expérience. Nous serons bientôt de retour !'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingMaintenance, setSavingMaintenance] = useState(false);

  useEffect(() => {
    document.title = 'Paramètres du Site | Admin';
    loadSettings();
    loadMaintenanceSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('settings')
        .select('*')
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      if (data) {
        setSettings(data);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error('Erreur lors du chargement des paramètres');
    } finally {
      setLoading(false);
    }
  };

  const loadMaintenanceSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('site_settings')
        .select('*')
        .eq('key', 'maintenance_mode')
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setMaintenance({
          enabled: data.value,
          message: data.message || 'Notre site est actuellement en maintenance pour vous offrir une meilleure expérience. Nous serons bientôt de retour !'
        });
      }
    } catch (error) {
      console.error('Error loading maintenance settings:', error);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setSettings(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setSaving(true);

      let response;

      if (settings.id) {
        // Update existing settings
        response = await supabase
          .from('settings')
          .update({
            companyName: settings.companyName,
            email: settings.email,
            phone: settings.phone,
            address: settings.address
          })
          .eq('id', settings.id);
      } else {
        // Insert new settings
        response = await supabase
          .from('settings')
          .insert([{
            companyName: settings.companyName,
            email: settings.email,
            phone: settings.phone,
            address: settings.address
          }]);
      }

      if (response.error) throw response.error;

      toast.success('Paramètres enregistrés avec succès');

      // Reload settings to get the updated data
      loadSettings();
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Erreur lors de l\'enregistrement des paramètres');
    } finally {
      setSaving(false);
    }
  };

  const handleMaintenanceToggle = async () => {
    try {
      setSavingMaintenance(true);

      const newEnabled = !maintenance.enabled;

      const { error } = await supabase
        .from('site_settings')
        .update({
          value: newEnabled,
          message: maintenance.message
        })
        .eq('key', 'maintenance_mode');

      if (error) throw error;

      setMaintenance(prev => ({ ...prev, enabled: newEnabled }));

      toast.success(
        newEnabled
          ? 'Mode maintenance activé - Le site affichera la page de maintenance'
          : 'Mode maintenance désactivé - Le site est de nouveau accessible'
      );
    } catch (error) {
      console.error('Error toggling maintenance:', error);
      toast.error('Erreur lors de la mise à jour du mode maintenance');
    } finally {
      setSavingMaintenance(false);
    }
  };

  const handleMaintenanceMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMaintenance(prev => ({ ...prev, message: e.target.value }));
  };

  const handleSaveMaintenanceMessage = async () => {
    try {
      setSavingMaintenance(true);

      const { error } = await supabase
        .from('site_settings')
        .update({
          message: maintenance.message
        })
        .eq('key', 'maintenance_mode');

      if (error) throw error;

      toast.success('Message de maintenance mis à jour');
    } catch (error) {
      console.error('Error saving maintenance message:', error);
      toast.error('Erreur lors de la mise à jour du message');
    } finally {
      setSavingMaintenance(false);
    }
  };

  if (loading) {
    return (
      <div className="px-6">
        <PageHeader
          title="Paramètres"
          description="Configurez les paramètres de votre site"
        />
        <LoadingState type="form" />
      </div>
    );
  }

  return (
    <div className="px-6">
      <PageHeader
        title="Paramètres"
        description="Configurez les paramètres de votre site"
      />
      
      <div className="space-y-6">
        {/* Mode Maintenance */}
        <ContentCard title="Mode Maintenance">
          <div className="space-y-6">
            <div className="flex items-start justify-between p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg border border-amber-200">
              <div className="flex items-start space-x-3 flex-1">
                <div className={`p-2 rounded-lg ${maintenance.enabled ? 'bg-red-100' : 'bg-green-100'}`}>
                  <Power className={`w-5 h-5 ${maintenance.enabled ? 'text-red-600' : 'text-green-600'}`} />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-gray-900 mb-1">
                    {maintenance.enabled ? 'Mode maintenance ACTIVÉ' : 'Mode maintenance désactivé'}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {maintenance.enabled
                      ? 'Le site affiche actuellement la page de maintenance. Les visiteurs ne peuvent pas accéder au contenu.'
                      : 'Le site est accessible normalement. Activez le mode maintenance pour afficher une page d\'information pendant les mises à jour.'
                    }
                  </p>
                </div>
              </div>

              <button
                onClick={handleMaintenanceToggle}
                disabled={savingMaintenance}
                className={`ml-4 relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                  maintenance.enabled ? 'bg-red-600' : 'bg-gray-200'
                } ${savingMaintenance ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    maintenance.enabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {maintenance.enabled && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                  <h4 className="text-sm font-semibold text-red-900">Attention</h4>
                </div>
                <p className="text-sm text-red-700">
                  Le mode maintenance est actuellement activé. N'oubliez pas de le désactiver une fois vos modifications terminées.
                </p>
              </div>
            )}

            <div>
              <label htmlFor="maintenanceMessage" className="block text-sm font-medium text-gray-700 mb-2">
                Message de maintenance
              </label>
              <textarea
                id="maintenanceMessage"
                value={maintenance.message}
                onChange={handleMaintenanceMessageChange}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                placeholder="Message qui sera affiché aux visiteurs pendant la maintenance..."
              />
              <p className="mt-2 text-xs text-gray-500">
                Ce message sera affiché sur la page de maintenance visible par les visiteurs.
              </p>
            </div>

            <div className="flex justify-end">
              <button
                onClick={handleSaveMaintenanceMessage}
                disabled={savingMaintenance}
                className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors shadow-sm text-sm font-medium disabled:opacity-50"
              >
                {savingMaintenance ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Enregistrement...
                  </>
                ) : (
                  <>
                    <Save size={16} className="mr-2" />
                    Enregistrer le message
                  </>
                )}
              </button>
            </div>
          </div>
        </ContentCard>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <ContentCard title="Informations Générales">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-1">
                  Nom de l'entreprise
                </label>
                <div className="relative">
                  <Globe size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    id="companyName"
                    name="companyName"
                    value={settings.companyName}
                    onChange={handleChange}
                    className="pl-10 pr-3 py-2 w-full border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Proxy Emobility"
                  />
                </div>
              </div>
              
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <div className="relative">
                  <Mail size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={settings.email}
                    onChange={handleChange}
                    className="pl-10 pr-3 py-2 w-full border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="hello@proxyemobility.com"
                  />
                </div>
              </div>
              
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                  Téléphone
                </label>
                <div className="relative">
                  <Phone size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={settings.phone}
                    onChange={handleChange}
                    className="pl-10 pr-3 py-2 w-full border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="+221 77 123 45 67"
                  />
                </div>
              </div>
              
              <div>
                <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
                  Adresse
                </label>
                <div className="relative">
                  <MapPin size={18} className="absolute left-3 top-3 text-gray-400" />
                  <textarea
                    id="address"
                    name="address"
                    value={settings.address}
                    onChange={handleChange}
                    rows={3}
                    className="pl-10 pr-3 py-2 w-full border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="123 Avenue de l'Indépendance, Dakar, Sénégal"
                  />
                </div>
              </div>
              
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors shadow-sm text-sm font-medium disabled:opacity-50"
                >
                  {saving ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Enregistrement...
                    </>
                  ) : (
                    <>
                      <Save size={16} className="mr-2" />
                      Enregistrer
                    </>
                  )}
                </button>
              </div>
            </form>
          </ContentCard>
        </div>
        
        <div>
          <ContentCard title="Informations">
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-1">Dernière mise à jour</h3>
                <p className="text-gray-900">
                  {settings.updatedAt 
                    ? new Date(settings.updatedAt).toLocaleString('fr-FR')
                    : 'Jamais mis à jour'}
                </p>
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-1">Conseil</h3>
                <p className="text-sm text-gray-600">
                  Ces informations sont utilisées à différents endroits sur votre site, notamment dans le pied de page et la page de contact.
                </p>
              </div>
            </div>
          </ContentCard>
        </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;